import React, { useCallback, useState } from 'react';
import moment from 'jalali-moment';

import { Box, Button, Calendar, Grid, Image, TextField, Select } from '@iho-design-system/Loader';

import CalendarSVG from '../../../assets/images/icons/calendar-blue.svg';
import CloseSVG from '../../../assets/images/icons/close.svg';

import { config } from '../../../api/apiConfig';
import { useFinance } from '../../../context/FinanceContext';

const Toolbar = ({ searchProps }) => {
    const { activeTab } = useFinance();

    const [searchData, setSearchData] = useState({
        startDate: '',
        endDate: '',
        serviceType: 1,
    });
    const [searchStartDate, setSearchStartDate] = useState('');
    const [searchEndDate, setSearchEndDate] = useState('');
    const [searchServiceType, setSearchServiceType] = useState(1);

    const buildDate = useCallback(
        (date) =>
            date !== ''
                ? moment.from(date, 'fa', 'YYYY/MM/DD').format('YYYY-MM-DD')
                : '',
        []
    );

    const search = useCallback(() => {
        const payload = {
            ...searchData,
            section: 'settlement',
            startDate: buildDate(searchStartDate),
            endDate: buildDate(searchEndDate),
            serviceType: searchServiceType,
        };
        setSearchData(payload);
        searchProps(payload);
    }, [searchData, searchStartDate, searchEndDate, searchServiceType, buildDate, searchProps]);

    const clearDate = useCallback((calendar) => {
        if (calendar === 'settlementStartDate') setSearchStartDate('');
        if (calendar === 'settlementEndDate') setSearchEndDate('');
    }, []);

    const downloadLink = useCallback(async () => {
        const AccessToken = localStorage.getItem('AccessToken');

        const startDate = buildDate(searchStartDate);
        const endDate = buildDate(searchEndDate);

        let url = '';

        if (activeTab === 1) {
            url = `${config.url}/api/mvc/export/GetCreditPaymentsExport?StartDate=${startDate}&EndDate=${endDate}`;
        } else if (activeTab === 2) {
            url = `${config.url}/api/mvc/export/GetPartialPayedReservesExport?StartDate=${startDate}&EndDate=${endDate}`;
        } else {
            return;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${AccessToken}`,
                },
            });

            if (!response.ok) throw new Error();

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download =
                activeTab === 1 ? 'wallet-report.xlsx' : 'partial-paid-reserves.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
        } catch {}
    }, [activeTab, buildDate, searchStartDate, searchEndDate]);

    const toggleCalendar = useCallback((event, calendar) => {
        if (event.target.tagName === 'DIV' || event.target.tagName === 'INPUT') {
            document.querySelector(`.${calendar}`).classList.toggle('d-none');
        }
    }, []);

    return (
        <Grid>
            <Grid.Wrapper>
                <Grid.Item className="position-relative" size="col-12 lg-2 md-4">
                    <Box onClick={(e) => toggleCalendar(e, 'settlementStartDate')}>
                        <TextField
                            variant="outlined"
                            label="از تاریخ"
                            value={searchStartDate}
                            placeholder="روز/ماه/سال"
                            ms={{ input: { paddingRight: '35px' } }}
                        />
                        <Image
                            className="pointer position-absolute"
                            src={CalendarSVG}
                            ms={{ width: '16px', height: '16px', top: '47px', right: '20px' }}
                        />
                        {searchStartDate !== '' && (
                            <Image
                                onClick={() => clearDate('settlementStartDate')}
                                className="pointer position-absolute"
                                src={CloseSVG}
                                ms={{ width: '16px', height: '16px', top: '47px', left: '20px' }}
                            />
                        )}
                        <Box
                            className="d-none settlementStartDate position-absolute"
                            ms={{ width: '320px', top: 'calc(75px + var(--sizing-xs))', zIndex: '1' }}
                        >
                            <Calendar onChange={setSearchStartDate} />
                        </Box>
                    </Box>
                </Grid.Item>

                <Grid.Item className="position-relative" size="col-12 lg-2 md-4">
                    <Box onClick={(e) => toggleCalendar(e, 'settlementEndDate')}>
                        <TextField
                            variant="outlined"
                            label="تا تاریخ"
                            value={searchEndDate}
                            placeholder="روز/ماه/سال"
                            ms={{ input: { paddingRight: '35px' } }}
                        />
                        <Image
                            className="pointer position-absolute"
                            src={CalendarSVG}
                            ms={{ width: '16px', height: '16px', top: '47px', right: '20px' }}
                        />
                        {searchEndDate !== '' && (
                            <Image
                                onClick={() => clearDate('settlementEndDate')}
                                className="pointer position-absolute"
                                src={CloseSVG}
                                ms={{ width: '16px', height: '16px', top: '47px', left: '20px' }}
                            />
                        )}
                        <Box
                            className="d-none settlementEndDate position-absolute"
                            ms={{ width: '320px', top: 'calc(75px + var(--sizing-xs))', zIndex: '1' }}
                        >
                            <Calendar onChange={setSearchEndDate} />
                        </Box>
                    </Box>
                </Grid.Item>

                {activeTab === 1 && (
                    <Grid.Item className="position-relative" size="col-12 lg-2 md-4">
                        <Select
                            onChange={setSearchServiceType}
                            label="نوع خدمات"
                            init={searchData.serviceType}
                        >
                            <Select.Item value="1">خدمات هتلی</Select.Item>
                            <Select.Item value="2">خدمات غیر هتلی</Select.Item>
                        </Select>
                    </Grid.Item>
                )}

                {activeTab === 2 && <Grid.Item size="col-12 lg-2 md-4" />}

                <Grid.Item className="d-flex justify-content-end" size="col-12 lg-6 md-4">
                    <Button
                        onClick={downloadLink}
                        variant="outlined"
                        color="primary"
                        ms={{
                            marginTop: '28px',
                            '@media(max-width:576px)': { marginTop: '0' },
                        }}
                    >
                        دانلود جدول
                    </Button>
                    <Box className="mx-xs" />
                    <Button
                        onClick={search}
                        color="primary"
                        ms={{
                            marginTop: '28px',
                            '@media(max-width:576px)': { marginTop: '0' },
                        }}
                    >
                        جستجو
                    </Button>
                </Grid.Item>
            </Grid.Wrapper>
        </Grid>
    );
};

export default Toolbar;
